import { useQuery } from '@tanstack/react-query';
import { getPropertyPaymentStatusHousesHouseIdPaymentStatusGetOptions } from '@/client/@tanstack/react-query.gen';
import { isHousePaymentValid } from '@/utils/paymentUtil';

/**
 * Hook for checking individual house payment status
 *
 * Why
 * - We gate the query with `enabled` so it only runs when a valid houseId exists.
 * - We expose v5-native flags (isPending, isFetching) plus a derived `isEnabled` for clarity
 *   in consumers like CurrentHouseProvider.
 * - `isPending` is the correct flag to show an initial loading state (no cached data yet).
 */
export const useHousePaymentStatus = (houseId?: number) => {
	const isEnabled = !!houseId;

	const {
		data: paymentStatus,
		isLoading,
		isPending,
		isFetching,
		error,
	} = useQuery({
		...getPropertyPaymentStatusHousesHouseIdPaymentStatusGetOptions({
			path: { house_id: houseId! },
		}),
		enabled: isEnabled,
	});

	// Determine if house is paid using utility function
	const isHousePaid = paymentStatus ? isHousePaymentValid(paymentStatus) : false;

	return {
		isHousePaid,
		// v5 flags
		isPending,
		isFetching,
		// keep isLoading for compatibility (alias to pending initial fetch)
		isLoading,
		isEnabled,
		error,
		paymentStatus,
	};
};
